import type {
  BenchmarkProfileCode,
  SflagaImprovementBenchmarkReport,
} from '../services/sflagaImprovementBenchmark';

const STORAGE_KEY = 'sflaga_improvement_benchmark_history_v1';
const MAX_HISTORY_ENTRIES = 8;

export interface SflagaImprovementBenchmarkHistoryEntry {
  id: string;
  timestamp: string;
  datasetSourceLabel: string;
  benchmarkCode?: BenchmarkProfileCode;
  report: SflagaImprovementBenchmarkReport;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
}

function readStoredHistoryEntries(): SflagaImprovementBenchmarkHistoryEntry[] {
  const storage = getStorage();
  if (!storage) {
    return [];
  }

  try {
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }

    return JSON.parse(stored) as SflagaImprovementBenchmarkHistoryEntry[];
  } catch (error) {
    console.error('Failed to load benchmark history:', error);
    return [];
  }
}

function resolveBenchmarkCode(
  entry: SflagaImprovementBenchmarkHistoryEntry
): BenchmarkProfileCode {
  return entry.benchmarkCode ?? entry.report.experiment.benchmarkCode ?? 'g';
}

export function getSflagaImprovementBenchmarkHistory(
  benchmarkCode: BenchmarkProfileCode = 'g'
): SflagaImprovementBenchmarkHistoryEntry[] {
  return readStoredHistoryEntries().filter(
    (entry) => resolveBenchmarkCode(entry) === benchmarkCode
  );
}

export function saveSflagaImprovementBenchmarkHistory(
  report: SflagaImprovementBenchmarkReport,
  datasetSourceLabel: string,
  benchmarkCode: BenchmarkProfileCode = report.experiment.benchmarkCode ?? 'g'
): string {
  const storage = getStorage();
  if (!storage) {
    return '';
  }

  const history = readStoredHistoryEntries();
  const entry: SflagaImprovementBenchmarkHistoryEntry = {
    id: `${benchmarkCode}_benchmark_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    timestamp: report.generatedAt,
    datasetSourceLabel,
    benchmarkCode,
    report,
  };

  history.unshift(entry);
  if (history.length > MAX_HISTORY_ENTRIES) {
    history.splice(MAX_HISTORY_ENTRIES);
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(history));
    return entry.id;
  } catch (error) {
    console.error('Failed to save benchmark history:', error);
    return '';
  }
}

export function removeSflagaImprovementBenchmarkHistoryEntry(id: string): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    const history = readStoredHistoryEntries().filter((entry) => entry.id !== id);
    storage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Failed to remove benchmark history entry:', error);
  }
}

export function clearSflagaImprovementBenchmarkHistory(
  benchmarkCode?: BenchmarkProfileCode
): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    if (!benchmarkCode) {
      storage.removeItem(STORAGE_KEY);
      return;
    }

    const remainingHistory = readStoredHistoryEntries().filter(
      (entry) => resolveBenchmarkCode(entry) !== benchmarkCode
    );

    if (remainingHistory.length === 0) {
      storage.removeItem(STORAGE_KEY);
      return;
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(remainingHistory));
  } catch (error) {
    console.error('Failed to clear benchmark history:', error);
  }
}
